import AuditSourceAPIService from 'frontend/data/sources/AuditSourceAPIService'
import useMemoizePromise from 'frontcore/utils/promises/memoize-promise'
import { AuditSource } from 'frontend/store/sources/types'
import { computed, ComputedRef, ref, Ref } from "vue"
import { defineStore } from 'pinia'
import { sortAlphabetically } from 'frontcore/utils/helpers/formatters'
import { useAlertQueueStore } from 'frontcore/presentation/alert/domain/useAlertQueueStore'

interface IAuditSourcesStore {
  auditSources: Ref<AuditSource[]>
  auditSourcesSorted: ComputedRef<AuditSource[]>
  masterAuditSources: ComputedRef<AuditSource[]>
  fetch: () => Promise<void>
  getAuditSourceByName: (name: string) => AuditSource | undefined
  getMasterAuditSourceById: (auditSourceId: number) => AuditSource | undefined
  isMasterAuditSource: (name: string | string[]) => boolean
  $reset: () => void
}

const useAuditSourcesStore = defineStore('auditSources', (): IAuditSourcesStore => {
  
  const auditSources = ref<AuditSource[]>([])

  const { memoize, reset } = useMemoizePromise<AuditSource[]>()
  const { showErrorMessage } = useAlertQueueStore()

  const fetch = async (): Promise<void> => {
    const apiService = new AuditSourceAPIService()
    try {
      auditSources.value = await memoize(apiService.fetch)
    } catch (error: unknown) {
      $reset()
      if (error instanceof Error) {
        showErrorMessage(error.message)
      }
      throw error
    }
  }
  (async () => { await fetch() })()

  const $reset = (): void => {
    reset()
    auditSources.value = []
  }

  const masterAuditSources = computed<AuditSource[]>(() => {
    return auditSources.value.filter((source) => source.isMasterAuditSource).sort(sortAlphabetically('name'))
  })

  const auditSourcesSorted = computed<AuditSource[]>(() => {
    return auditSources.value.sort((a, b) => {
      if (a.isMasterAuditSource === b.isMasterAuditSource) {
        return a.name.localeCompare(b.name)
      }
      return a.isMasterAuditSource ? 1 : -1
    })
  })

  const getMasterAuditSourceById = (auditSourceId: number) => {
    return masterAuditSources.value.find((source) => source.id === auditSourceId)
  }

  const getAuditSourceByName = (name: string) => {
    return auditSources.value.find((source) => source.name === name)
  }

  const isMasterAuditSource = (name: string | string[]) => {
    const _source = Array.isArray(name) ? name[0] : name
    return auditSources.value.find((source) => source.name === _source)?.isMasterAuditSource ?? false
  }

  return {
    auditSources,
    auditSourcesSorted,
    masterAuditSources,
    fetch,
    getAuditSourceByName,
    getMasterAuditSourceById,
    isMasterAuditSource,
    $reset,
  }
})

export default useAuditSourcesStore
